import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ThemeProviderWrapper } from './contexts/ThemeContext';

// Components
import Login from './components/Auth/Login';
import Register from './components/Auth/Register';
import RegisterStep2 from './components/Auth/RegisterStep2';
import RegisterStep3 from './components/Auth/RegisterStep3';
import RegisterSummary from './components/Auth/RegisterSummary';
import ProfilePage from './components/Dashboard/ProfilePage';
import CreateCompanyProfile from './components/Dashboard/CreateCompanyProfile';
import SiteDetailsV2 from './components/Sites/SiteDetailsV2';

// Dashboard Views
import ReportsTab from './components/Dashboard/tabs/ReportsTab';
import SitesPage from './components/Dashboard/SitesPage';
import UsersTab from './components/Dashboard/tabs/UsersTab';
import SettingsTab from './components/Dashboard/tabs/SettingsTab';
import ProfilesTab from './components/Dashboard/tabs/ProfilesTab';
import MainLayout from './components/Layout/MainLayout';
import { SubscriptionSuccess, SubscriptionCancel } from './components/Billing/SubscriptionReturnPages';
import PrivacyPolicyPage from './components/Legal/PrivacyPolicyPage';

import './App.css';

function App() {
  return (
    <ThemeProviderWrapper>
      <AuthProvider>
        <Router>
          <AppRoutes />
        </Router>
      </AuthProvider>
    </ThemeProviderWrapper>
  );
}

export const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { currentUser } = useAuth();
  return currentUser ? <>{children}</> : <Navigate to="/login" />;
};

export const AdminRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { currentUser, userProfile } = useAuth();
  if (!currentUser) return <Navigate to="/login" />;
  return userProfile?.role === 'Admin' ? <>{children}</> : <Navigate to="/profile" replace />;
};

export const PublicRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { currentUser } = useAuth();
  return !currentUser ? <>{children}</> : <Navigate to="/profile" />;
};

// Main App Routes
const AppRoutes: React.FC = () => {
  return (
    <Routes>
      <Route
        path="/login"
        element={
          <PublicRoute>
            <Login />
          </PublicRoute>
        }
      />
      <Route
        path="/register"
        element={
          <PublicRoute>
            <Register />
          </PublicRoute>
        }
      />
      <Route
        path="/register/step2"
        element={
          <PublicRoute>
            <RegisterStep2 />
          </PublicRoute>
        }
      />
      <Route
        path="/register/step3"
        element={
          <PublicRoute>
            <RegisterStep3 />
          </PublicRoute>
        }
      />
      {/* Not wrapped in PublicRoute: createUser signs in before Firestore writes; redirecting would unmount and skip setDoc. */}
      <Route path="/register/summary" element={<RegisterSummary />} />
      <Route
        path="/profile"
        element={
          <ProtectedRoute>
            <ProfilePage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/create-profile"
        element={
          <ProtectedRoute>
            <CreateCompanyProfile />
          </ProtectedRoute>
        }
      />
      <Route
        path="/sites/:siteId"
        element={
          <ProtectedRoute>
            <SiteDetailsV2 />
          </ProtectedRoute>
        }
      />
      <Route
        path="/sites"
        element={
          <ProtectedRoute>
            <SitesPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/reports"
        element={
          <ProtectedRoute>
            <MainLayout title="Reports">
              <ReportsTab />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/users"
        element={
          <AdminRoute>
            <MainLayout title="User Management">
              <UsersTab />
            </MainLayout>
          </AdminRoute>
        }
      />
      <Route
        path="/profiles"
        element={
          <ProtectedRoute>
            <MainLayout title="Card Reader Profiles">
              <ProfilesTab />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings"
        element={
          <ProtectedRoute>
            <MainLayout title="Platform Settings">
              <SettingsTab />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      {/* Public: App Store / legal / unauthenticated review */}
      <Route path="/privacy" element={<PrivacyPolicyPage />} />
      {/* Public: user may return from Stripe before Firebase account exists (register-first-checkout flow). */}
      <Route path="/subscription/success" element={<SubscriptionSuccess />} />
      <Route path="/subscription/cancel" element={<SubscriptionCancel />} />
      <Route path="/" element={<Navigate to="/profile" />} />
    </Routes>
  );
};

export default App;
